import { grapheneTimeDeltaToNumeric } from '@/utils/time'
import { parseISO } from 'date-fns'
import type { ResultOf } from 'gql.tada'
import type { SALARY_MONTH_SUMMARY } from '../queries'

type MonthReport = NonNullable<
  ResultOf<typeof SALARY_MONTH_SUMMARY>['monthReport']
>

/**
 * Aggregated month totals used to populate the salary "Sum for [month]"
 * summary cards. All time values are seconds (re-emit with
 * `secondsToHhmm` for display).
 */
export interface SalaryMonthSummary {
  totalWorkSeconds: number
  drivingSeconds: number
  otherWorkSeconds: number
  availabilitySeconds: number
  pauseSeconds: number
  restSeconds: number
  paidRestSeconds: number
  /** Overtime seconds attributed to the current month. */
  overtimeSeconds: number
  /**
   * Overtime seconds attributed to the next month (only relevant when
   * `useWeekOvertime` is enabled for the user; rendered as a "+N next
   * month" hint below the overtime value).
   */
  overtimeNextMonthSeconds: number
  /**
   * Whether any WorkPeriod uses week-based overtime — controls whether
   * the "next month" hint is shown at all.
   */
  hasUseWeekOvertime: boolean
}

const emptySummary = (): SalaryMonthSummary => ({
  totalWorkSeconds: 0,
  drivingSeconds: 0,
  otherWorkSeconds: 0,
  availabilitySeconds: 0,
  pauseSeconds: 0,
  restSeconds: 0,
  paidRestSeconds: 0,
  overtimeSeconds: 0,
  overtimeNextMonthSeconds: 0,
  hasUseWeekOvertime: false,
})

function toSec(timeDelta: string | null | undefined): number {
  if (!timeDelta) return 0
  return grapheneTimeDeltaToNumeric(timeDelta) ?? 0
}

/**
 * Format an aggregated second count as zero-padded `HH:MM`. Hours are
 * not capped at 24, so a 154-hour month renders as `154:07`.
 */
export function formatSecondsAsHhmm(totalSeconds: number): string {
  const sign = totalSeconds < 0 ? '-' : ''
  const abs = Math.abs(totalSeconds)
  const hours = Math.floor(abs / 3600)
  const minutes = Math.floor((abs % 3600) / 60)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${sign}${pad(hours)}:${pad(minutes)}`
}

/**
 * Aggregate per-WorkPeriod activity times plus monthly overtime sums
 * into a single `SalaryMonthSummary`.
 *
 * Walks every WorkPeriod nested inside `monthReport.workPeriodGroups`
 * and sums the time-category fields directly. Overtime is summed from
 * `monthReport.overtime[]`, partitioned by the `nextMonth` flag so the
 * UI can render the "+N next month" hint.
 *
 * The backend's `monthReport` resolver expands the requested date range
 * to full ISO weeks so it can compute week-based overtime correctly. As
 * a side effect, `workPeriodGroups[].workPeriods[]` can include WPs from
 * the trailing/leading week that falls outside the user-selected month.
 * Callers should pass `rangeStart`/`rangeEnd` so we can filter those
 * out — otherwise activity-time totals would include WPs the user
 * doesn't see in the approve list.
 *
 * - `null`/`undefined` `monthReport` → zeroed summary.
 * - Missing/empty TimeDelta strings contribute 0.
 * - WorkPeriods whose `datetimeStart` is outside `[rangeStart, rangeEnd)`
 *   are skipped (range filter only applies when both bounds are given).
 *
 * @param monthReport `monthReport` payload from `SALARY_MONTH_SUMMARY`.
 * @param rangeStart Lower bound (inclusive) for WP `datetimeStart`.
 * @param rangeEnd Upper bound (inclusive) for WP `datetimeStart`.
 */
export function aggregateMonthSummary(
  monthReport: MonthReport | null | undefined,
  rangeStart?: Date,
  rangeEnd?: Date
): SalaryMonthSummary {
  const result = emptySummary()
  if (!monthReport) return result

  result.totalWorkSeconds = toSec(monthReport.totalWorkTime)

  const startMs = rangeStart?.getTime()
  const endMs = rangeEnd?.getTime()

  // A WorkPeriod can appear in multiple `workPeriodGroups` (e.g. grouped
  // by vehicle, calendar item, or absence day). Dedupe by `id` so its
  // activity times aren't summed twice.
  const seen = new Set<string>()
  for (const group of monthReport.workPeriodGroups ?? []) {
    for (const wp of group.workPeriods ?? []) {
      if (seen.has(wp.id)) continue
      seen.add(wp.id)

      if (startMs !== undefined && endMs !== undefined) {
        const wpMs = parseISO(wp.datetimeStart).getTime()
        if (wpMs < startMs || wpMs > endMs) continue
      }

      const t = wp.activityTimes
      if (t) {
        result.drivingSeconds += toSec(t.timeDriving)
        result.otherWorkSeconds += toSec(t.timeOtherWork)
        result.availabilitySeconds += toSec(t.timeAvailability)
        result.pauseSeconds += toSec(t.timePause)
        result.restSeconds += toSec(t.timeRest)
        result.paidRestSeconds += toSec(t.timePaidRest)
      }
      if (wp.salarySetting?.useWeekOvertime) {
        result.hasUseWeekOvertime = true
      }
    }
  }

  for (const entry of monthReport.overtime ?? []) {
    const sec = toSec(entry.sum)
    if (entry.nextMonth) result.overtimeNextMonthSeconds += sec
    else result.overtimeSeconds += sec
  }

  return result
}
